import React, { useCallback, useMemo, useState } from "react";
import {
  Badge,
  Callout,
  ChartSkeleton,
  formatDate,
  PageContent,
  PageHeader,
  PageLayout,
} from "../../ui";
import { ClusterScatter } from "./components/ClusterScatter";
import { FailureClustersEmptyState } from "./components/EmptyState";
import { GranularityToggle } from "./components/GranularityToggle";
import { ModeDetailPanel } from "./components/ModeDetailPanel";
import { ModeLegend } from "./components/ModeLegend";
import { TimeSlider } from "./components/TimeSlider";
import { useClusteringMap } from "./hooks/useClusteringMap";
import { useModeColors } from "./hooks/useModeColors";
import { useSelectedMode } from "./hooks/useSelectedMode";
import { useVisibleModes } from "./hooks/useVisibleModes";
import type { Granularity } from "./types";
import { snapValue } from "./utils/timeSlider";
import { windowLabel } from "./utils/windowCopy";

// Failure Clusters is a newly launched feature still in Beta (design doc 015).
const BETA_BADGE = <Badge variant="warning">Beta</Badge>;

/**
 * Failure Clusters page (design doc 015 §6): a per-customer map of recurring
 * failure modes — coloured scatter + the mode legend (the heart of the page) —
 * with a daily/weekly window toggle. Read only; the API does all aggregation
 * server-side.
 */
export default function FailureClustersPage() {
  const [granularity, setGranularity] = useState<Granularity>("weekly");
  // null = the live "now" view; an ISO string scrubs the window back in time.
  const [asOf, setAsOf] = useState<string | null>(null);
  const { response, loading, error } = useClusteringMap(granularity, asOf);
  const { selectedModeId, selectMode, clearMode } = useSelectedMode();

  // Map de-cluttering (#1297): start with the largest few modes shown and let the
  // legend toggle the rest; the long, tiny-mode tail otherwise overlaps into mud.
  const modes = useMemo(() => response?.modes ?? [], [response]);
  const { visibleModeIds, toggle, ensureVisible, showAll, hideAll, resetTop } =
    useVisibleModes(modes);
  // Session-stable colours: the modes drawn on the map get distinct palette
  // colours that hold across panning, the daily/weekly toggle, and the panels.
  const { colorForMode } = useModeColors(modes, visibleModeIds);
  const [showUnassigned, setShowUnassigned] = useState(false);

  // Selecting a mode (legend row or map point) reveals it if it was toggled off,
  // so the highlight never points at an undrawn cluster.
  const handleSelectMode = useCallback(
    (id: string) => {
      ensureVisible(id);
      selectMode(id);
    },
    [ensureVisible, selectMode],
  );

  const visibleModes = useMemo(
    () => modes.filter((m) => visibleModeIds.has(m.failure_mode_id)),
    [modes, visibleModeIds],
  );

  const selectedMode =
    response?.modes.find((m) => m.failure_mode_id === selectedModeId) ?? null;

  // Switching daily/weekly while scrubbed: re-snap asOf onto the new
  // granularity's stop grid so the slider thumb and the fetched window stay in
  // lockstep (otherwise asOf lags the snapped thumb until the next drag).
  const handleGranularityChange = (next: Granularity) => {
    if (asOf !== null && response?.available_from) {
      setAsOf(snapValue(next, asOf, response.available_from));
    }
    setGranularity(next);
  };

  // Not enabled: clustering is off for this customer (#1257) — show the
  // contact-support prompt rather than a blank map or a misleading cold start.
  const isNotEnabled = response !== null && !response.clustering_enabled;
  // Cold start: the domain has never completed a discovery, so there are no
  // modes and no positioned points yet (§5.3 — last_discovery_at is null).
  // last_discovery_at is domain-global (not windowed by the scrub), so a
  // scrubbed window over backfilled history never reads as cold start (#1263).
  const isColdStart =
    response !== null && !isNotEnabled && response.last_discovery_at === null;
  const isEmpty =
    response !== null &&
    !isNotEnabled &&
    !isColdStart &&
    response.modes.length === 0 &&
    response.points.length === 0 &&
    response.unassigned_count === 0;

  return (
    <PageLayout>
      <PageContent maxWidth="full">
        <PageHeader
          title="Failure Clusters"
          description="Your recurring failure modes, named, sized, and tracked over time."
          badge={
            <div className="flex items-center gap-3">
              {BETA_BADGE}
              <GranularityToggle
                value={granularity}
                onChange={handleGranularityChange}
              />
            </div>
          }
        />

        {error ? (
          <Callout className="border-destructive/40 text-destructive">
            {error}
          </Callout>
        ) : !response ? (
          <ChartSkeleton showTitle={false} height="560px" />
        ) : (
          <div className="space-y-4">
            {/* The window summary + history slider sit above the content.
                Hidden when clustering isn't enabled and for a genuine cold
                start (the domain has never completed a discovery), where
                there is nothing to scrub. */}
            {!isNotEnabled && (asOf !== null || !isColdStart) && (
              <div className="flex flex-col gap-3">
                <p className="text-sm text-muted-foreground">
                  {asOf
                    ? `${formatDate(response.window_start)} – ${formatDate(response.window_end)}`
                    : windowLabel(granularity)}
                  {response.last_discovery_at && (
                    <> · Last discovery {formatDate(response.last_discovery_at)}</>
                  )}
                  {loading && <span className="ml-2 italic">Updating…</span>}
                </p>
                <TimeSlider
                  granularity={granularity}
                  availableFrom={response.available_from}
                  value={asOf}
                  onChange={setAsOf}
                />
              </div>
            )}

            {isNotEnabled ? (
              <FailureClustersEmptyState variant="not-enabled" />
            ) : isColdStart ? (
              <FailureClustersEmptyState variant="cold-start" />
            ) : isEmpty ? (
              <FailureClustersEmptyState variant="no-data" />
            ) : (
              <>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                  <div className="h-[560px] rounded-lg border border-border bg-card p-2 lg:col-span-2">
                    <ClusterScatter
                      points={response.points}
                      modes={visibleModes}
                      selectedModeId={selectedModeId}
                      onSelectMode={handleSelectMode}
                      colorForMode={colorForMode}
                      showUnassigned={showUnassigned}
                    />
                  </div>
                  <div className="h-[560px] lg:col-span-1">
                    <ModeLegend
                      modes={response.modes}
                      selectedModeId={selectedModeId}
                      onSelect={handleSelectMode}
                      colorForMode={colorForMode}
                      visibleModeIds={visibleModeIds}
                      onToggleVisibility={toggle}
                      onShowAll={showAll}
                      onHideAll={hideAll}
                      onResetTop={resetTop}
                      unassignedCount={response.unassigned_count}
                      showUnassigned={showUnassigned}
                      onToggleUnassigned={() => setShowUnassigned((v) => !v)}
                      granularity={granularity}
                      isLive={asOf === null}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        <ModeDetailPanel
          mode={selectedMode}
          points={response?.points ?? []}
          onClose={clearMode}
          colorForMode={colorForMode}
          granularity={granularity}
          isLive={asOf === null}
        />
      </PageContent>
    </PageLayout>
  );
}
